<script setup lang="ts">
import { computed, ref } from 'vue';
import { Eye, Pencil, Trash2 } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { LazyTooltip } from '@/components/ui/tooltip';
import { usePermissions } from '@/composables/usePermissions';
import { isIndexedSchedule } from '@/components/arrayColumns/scheduleCell';
import { isEditableKind, scheduleKind, type ScheduleRow } from '@/utils/customPlaylistSchedule';
import ScheduleEditorDialog from './ScheduleEditorDialog.vue';
import ScheduleDeleteDialog from './ScheduleDeleteDialog.vue';
import { positiveZoneId } from './scheduleGroups';

/**
 * Edit and Delete for one schedule, with the dialogs they open. The inspector's Schedules
 * section and the search cards both draw them, beside the schedule, when the pointer is over it.
 *
 * The buttons follow the location_zone_custom_playlist_schedule model permission. The server's
 * own row check still decides, and a refusal shows a plain message in the dialog. A station
 * change cannot be edited here, so it offers View instead of Edit. Delete needs a schedule the
 * index has written in full, because a half-written one has no id to delete by.
 *
 * The dialogs are only mounted once one is first opened: a list of fifty schedules does not
 * build a hundred closed dialogs.
 */
const props = withDefaults(
    defineProps<{
        schedule: ScheduleRow;
        listId: unknown;
        listName?: string | null;
        /** The list's own zone. */
        ownerZoneId: unknown;
        ownerZoneName?: string | null;
        /** The schedule's own zone, for the delete dialog's zone line. */
        zoneId?: number | null;
        zoneName?: string | null;
    }>(),
    { listName: null, ownerZoneName: null, zoneId: null, zoneName: null },
);

const { can } = usePermissions();
const PERMISSION_KEY = 'location_zone_custom_playlist_schedule';
const canUpdate = computed(() => can('update', PERMISSION_KEY));
const canDelete = computed(() => can('delete', PERMISSION_KEY));

const listIdNumber = computed(() => positiveZoneId(props.listId));
const ownerId = computed(() => positiveZoneId(props.ownerZoneId));
/** Writing needs both ids. Without them nothing is offered. */
const writable = computed(() => listIdNumber.value !== null && ownerId.value !== null);

const isEditable = computed(() => isEditableKind(scheduleKind(props.schedule)));
const zoneId = computed(() => props.zoneId ?? positiveZoneId(props.schedule.location_zone_id));

const editorOpen = ref(false);
const deleteOpen = ref(false);
const editorMounted = ref(false);
const deleteMounted = ref(false);

function openEditor(): void {
    editorMounted.value = true;
    editorOpen.value = true;
}

function openDelete(): void {
    deleteMounted.value = true;
    deleteOpen.value = true;
}
</script>

<template>
    <span v-if="writable" class="inline-flex shrink-0 items-center gap-0.5" data-testid="schedule-row-actions">
        <LazyTooltip v-if="canUpdate && isEditable">
            <Button
                type="button"
                variant="ghost"
                size="icon"
                class="size-7"
                aria-label="Edit schedule"
                data-testid="schedule-edit"
                @click.stop="openEditor"
            >
                <Pencil class="size-3.5" aria-hidden="true" />
            </Button>
            <template #content>Edit schedule</template>
        </LazyTooltip>
        <LazyTooltip v-else>
            <Button
                type="button"
                variant="ghost"
                size="icon"
                class="size-7"
                aria-label="View schedule"
                data-testid="schedule-view"
                @click.stop="openEditor"
            >
                <Eye class="size-3.5" aria-hidden="true" />
            </Button>
            <template #content>View schedule</template>
        </LazyTooltip>
        <LazyTooltip v-if="canDelete && isIndexedSchedule(schedule)">
            <Button
                type="button"
                variant="ghost"
                size="icon"
                class="size-7"
                aria-label="Delete schedule"
                data-testid="schedule-delete"
                @click.stop="openDelete"
            >
                <Trash2 class="size-3.5" aria-hidden="true" />
            </Button>
            <template #content>Delete schedule</template>
        </LazyTooltip>

        <ScheduleEditorDialog
            v-if="editorMounted"
            v-model:open="editorOpen"
            :schedule="schedule"
            :list-id="listIdNumber!"
            :list-name="listName"
            :owner-zone-id="ownerId!"
            :owner-zone-name="ownerZoneName"
            :read-only="!canUpdate"
        />
        <ScheduleDeleteDialog
            v-if="deleteMounted"
            v-model:open="deleteOpen"
            :schedule="schedule"
            :list-id="listIdNumber!"
            :zone-id="zoneId"
            :zone-name="zoneName"
        />
    </span>
</template>
